import React, { useEffect, useState } from "react";

import { FormField } from "@avamae/formbuilder";
import { useField, useFormikContext } from "formik";
import { FormFieldTooltip } from "components/helpers/FormFieldTooltip/FormFieldTooltip";
import UploadIcon from "assets/Icon_Upload_Cloud.png";
import Dropzone from "react-dropzone";
import PDFViewer from "components/PdfViewer/PdfViewer";
import { pascalCaseToCamelCase } from "helpers/pascalCaseToCamelCase";

const tooltipMainIconSource = require("assets/Tooltip.png");
const tooltipCloseIconSource = require("assets/Icon_Btn_06.png");

export const MAX_FILE_SIZE = 500000000;

export const ALL_FIELDS_KEY = "ALL_FIELDS";

export type AcceptedFileTypes = { [key: string]: string[] }; // MIME File Types

type FileUploadProps = {
  name: string;
  dataSource: string | null;
  label: string;
  tooltip: string | null;
  isMulti?: boolean;
  acceptedFileTypes: AcceptedFileTypes;
};

export type FileType = {
  fileName: string;
  data: File;
  preview: string;
};
export const FileUpload: React.FC<FileUploadProps> = ({
  name,
  dataSource,
  label,
  tooltip,
  isMulti,
  acceptedFileTypes,
}) => {
  const [field] = useField<any>(name);
  const [value, setValue] = useState<any>(field.value);
  const [file, setFile] = useState<FileType | null>(null);

  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [newUploadToggle, setNewUploadToggle] = useState<boolean>(false); //optional
  const [relevantFormikError, setRelevantFormikError] = useState<any>(null);
  const [thumbnails, setThumbnails] = useState<any>(null);

  const fieldAcceptedFileTypes = acceptedFileTypes.hasOwnProperty(field.name)
    ? acceptedFileTypes[field.name]
    : acceptedFileTypes[ALL_FIELDS_KEY];

  const { values, errors, setFieldValue, submitCount, setFieldError, setErrors } =
    useFormikContext<{}>();

  const clearFieldError = () => {
    let newErrors = { ...errors };

    if (newErrors.hasOwnProperty(field.name)) {
      delete newErrors[field.name as keyof typeof newErrors];
    }

    setErrors(newErrors);
  };

  useEffect(() => {
    if (errors.hasOwnProperty(field.name)) {
      const error = errors[field.name as keyof typeof errors];

      if (typeof error === "string") {
        setRelevantFormikError(errors[field.name as keyof typeof errors]);
      }
    } else {
      setRelevantFormikError(null);
    }
  }, [errors]);

  const onDrop = (acceptedFiles: File[]) => {
    clearFieldError();

    let fileName = "";
    const reader = new FileReader();

    reader.onabort = () => console.log("File reading was aborted");
    reader.onerror = () => console.log("File reading has failed");

    if (acceptedFiles.length > 1) {
      setTimeout(() => {}, 2500);
      return;
    }

    acceptedFiles.forEach((file) => {
      fileName = file.name;
      if (!isMulti)
        setFile({
          data: file,
          fileName,
          preview: URL.createObjectURL(file),
        }); //last element of files array (acceptedFiles)
    });

    setNewUploadToggle(!newUploadToggle);
  };

  useEffect(() => {
    if (file && file.data && !isMulti) {
      setFieldValue(field.name, file.data);
    }
  }, [newUploadToggle]);

  const validateFile = (file: File) => {
    let errors: string[] = [];

    if (!fieldAcceptedFileTypes.includes(file.type)) {
      const messageCode = "PleaseSelectAValidFileType";
      errors = [...errors, messageCode];
    }
    if (MAX_FILE_SIZE && file.size > MAX_FILE_SIZE) {
      const messageCode = "TheSelectedFileExceedsTheMaximumFileSize";
      errors = [...errors, messageCode];
    }

    setFileErrors(errors);
  };

  useEffect(() => {
    if (file === null) {
      setThumbnails(null);
    } else if (file && file.fileName && file.preview) {
      const parsedFileName = file.fileName.split(".");
      if (parsedFileName[parsedFileName.length - 1] === "pdf") {
        setThumbnails(
          <PDFViewer file={file} fileName={file.fileName} showTitle={false} overflowHidden={true} />
        );
      } else {
        setThumbnails(
          <div className="Thumbnails" key={file?.fileName}>
            <div className="ThumbnailsInner">
              <img
                src={file?.preview}
                className="ThumbnailsImage"
                // Revoke data uri after image is loaded
                onLoad={() => {
                  URL.revokeObjectURL(file?.preview);
                }}
                alt="Uploaded file preview"
              />
            </div>
          </div>
        );
      }
    }
  }, [file]);

  const acceptedFileTypesToDisplay =
    fieldAcceptedFileTypes &&
    fieldAcceptedFileTypes
      .map((item) => {
        let parsedItem = item.split("/")[1];
        if (parsedItem === "msword") {
          parsedItem = "word";
        }
        return parsedItem;
      })
      .join(", ");

  console.log("acceptedFileTypesToDisplay: ", acceptedFileTypesToDisplay);

  const resetFile = () => {
    if (
      file &&
      values &&
      values.hasOwnProperty(field.name) &&
      values[field.name as keyof typeof values] !== null
    ) {
      // Reset file field
      setFieldValue(field.name, null);

      // Reset fileName field if it exists
      if (values.hasOwnProperty("fileName")) {
        setFieldValue("fileName", null);
      }

      // Clear file state
      setFile(null);
    }
  };

  const getExistingFile = async (url: string, fileName: string) => {
    fetch(url) //field.value or field.value[field.value?.length-1]
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], fileName, { type: blob.type });

        setFile({
          data: file, // file initialVal
          fileName,
          preview: URL.createObjectURL(file),
        });
      });
  };

  useEffect(() => {
    if (values.hasOwnProperty(field.name) && !file) {
      const url = values[field.name as keyof typeof values];
      const fileName = values.hasOwnProperty("filename")
        ? values["filename" as keyof typeof values]
        : values.hasOwnProperty("fileName")
          ? values["fileName" as keyof typeof values]
          : null;

      if (url && typeof url === "string") {
        getExistingFile(url, fileName ? fileName : "test.png");
      }
    }
  }, [values]);

  return (
    <div className={`FormBox FormSideBySide FileUploadFieldRendererFormBox`}>
      <div className={`FormLabel`}>
        <label>{label}</label>

        {tooltip && (
          <FormFieldTooltip
            tooltipText={tooltip}
            tooltipMainIconSource={tooltipMainIconSource}
            tooltipCloseIconSource={tooltipCloseIconSource}
          />
        )}
      </div>
      <div className={`FormField`}>
        <div className={`FileUploadFieldRenderer ${relevantFormikError ? "Error" : ""}`}>
          <div className="DataProcessing">
            (Note that it may take a few minutes for documents to be uploaded...)
          </div>

          <Dropzone
            onDrop={(acceptedFiles, rejectedFiles, event) => onDrop(acceptedFiles)}
            onDropRejected={(files) => validateFile(files[0])}
            onDropAccepted={() => setFileErrors([])}
            accept={fieldAcceptedFileTypes}
            maxSize={MAX_FILE_SIZE}
          >
            {({ getRootProps, getInputProps }) => (
              <div className={`DropzoneContainer`} {...getRootProps()}>
                <div className="Contents">
                  <img className={`UploadIcon`} src={UploadIcon} alt="Upload" />
                  <input {...getInputProps()} />

                  <h3>File</h3>
                  <span>Click or drag to upload</span>
                </div>
                {thumbnails && <div className="ThumbnailsContainer">{thumbnails}</div>}
              </div>
            )}
          </Dropzone>

          <div className="SubContainers">
            <div className="SubContainer Container1">
              <div className="SubContainerItem">
                <div className="Title">{"Accepts - "}</div>
                <div className="Text">{acceptedFileTypesToDisplay}</div>
              </div>

              <div className="SubContainerItem">
                <div className="Title">{"Recommended size - "}</div>
                <div className="Text">500x200px</div>
              </div>
            </div>

            <div className="SubContainer Container2">
              <div className={`Btn ResetBtn ${!file ? "Disabled" : ""}`} onClick={resetFile}>
                Reset
              </div>
            </div>
          </div>

          {thumbnails && file && file.fileName && (
            <div className="UploadedFile">
              <div className="Title">Uploaded file: </div>
              <div className="Text">{file.fileName}</div>
            </div>
          )}
        </div>

        {relevantFormikError && submitCount > 0 && (
          <div className="ErrorLabel">{relevantFormikError}</div>
        )}
      </div>
    </div>
  );
};

type FileUploadReadOnlyProps = {
  name: string;
  label: string;
  tooltip: string | null;
  isMulti?: boolean;
};

const FileUploadReadOnly: React.FC<FileUploadReadOnlyProps> = ({
  name,
  label,
  tooltip,
  isMulti,
}) => {
  const [field] = useField<any>(name);
  const [uploadedFile, setUploadedFile] = useState<{
    file: string;
    fileName: string;
  } | null>(null);
  const [fileFormat, setFileFormat] = useState<"pdf" | "image" | null>(null);

  useEffect(() => {
    if (field.value && typeof field.value === "string" && field.name) {
      const file = field.value;
      const parsedFileName = file.split(".");

      const lastElement = parsedFileName[parsedFileName.length - 1];

      const tempFileFormat = lastElement.split("?")[0];

      if (tempFileFormat && typeof tempFileFormat === "string") {
        setFileFormat(
          tempFileFormat === "pdf" || tempFileFormat === "doc" || tempFileFormat === "docx"
            ? "pdf"
            : "image"
        );

        setUploadedFile({ file: field.value, fileName: field.name });
      }
    }
  }, [field.value]);

  useEffect(() => {});

  return (
    <div className={`FormBox FormSideBySide `}>
      <div className="FormLabel">
        <label>{label}</label>

        {tooltip && (
          <FormFieldTooltip
            tooltipText={tooltip}
            tooltipMainIconSource={tooltipMainIconSource}
            tooltipCloseIconSource={tooltipCloseIconSource}
          />
        )}
      </div>

      <div className="FormField">
        <div className={`FileUploadFieldRenderer ReadOnly`}>
          {uploadedFile && fileFormat ? (
            fileFormat === "pdf" ? (
              <PDFViewer
                file={uploadedFile.file}
                fileName={uploadedFile?.fileName}
                showTitle={false}
              />
            ) : (
              <div className="ThumbnailsContainer">
                <div className="Thumbnails" key={uploadedFile?.fileName}>
                  <div className="ThumbnailsInner">
                    <img
                      src={uploadedFile?.file}
                      className="ThumbnailsImage"
                      // Revoke data uri after image is loaded
                      onLoad={() => {
                        URL.revokeObjectURL(uploadedFile?.file);
                      }}
                      alt="Uploaded file preview"
                    />
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="NoData">
              <div className="Text">No file uploaded</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const fileUploadFieldRenderer = (
  readOnly: boolean,
  acceptedFileTypes?: AcceptedFileTypes // MIME File Types
) =>
  function fileUploadFieldRenderer<T>(_key: React.ReactText, field: FormField<T>) {
    let tempAcceptedFileTypes: AcceptedFileTypes = { ...acceptedFileTypes };

    if (!acceptedFileTypes) {
      tempAcceptedFileTypes[ALL_FIELDS_KEY] = [
        "application/msword",
        "application/pdf",
        "image/jpg",
        "image/png",
      ]; // MIME File Types
    }

    console.log("tempAcceptedFileTypes: ", tempAcceptedFileTypes);

    if (readOnly) {
      return (
        <FileUploadReadOnly
          name={String(field.name)}
          label={field.label}
          tooltip={field.toolTip ? field.toolTip : null}
        />
      );
    } else {
      return (
        <FileUpload
          name={String(field.name)}
          dataSource={
            field.dataSource && typeof field.dataSource === "string"
              ? pascalCaseToCamelCase(field.dataSource)
              : null
          }
          label={field.label}
          tooltip={field.toolTip ? field.toolTip : null}
          acceptedFileTypes={tempAcceptedFileTypes}
        />
      );
    }
  };
